import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';
import { ApiCallBase } from '../apiCallBase';
import { API_V3_URL } from 'src/app/config/config';
import { LanguageService } from 'src/app/services/language/language.service';

@Injectable({
  providedIn: 'root',
})
export class TutoriaService extends ApiCallBase {
  correctedLanguage = 'EN-US';
  constructor(
    public languageService: LanguageService,
    public http: HttpClient,
    public router: Router,
  ) {
    super(languageService, http, router);
    this.correctedLanguage = this.language === 'es' ? 'ES-LA' : 'EN-US';
  }

  generateTutorIAResponseV2(
    body: CreateChatCompletionForTutorIAV2_Req,
    token: string,
  ) {
    const url = `${API_V3_URL}/tutoria-ai/v2/chat`;
    const apiV3Options = this.getHttpOptionsV3WithToken(token);

    const res = this.http
      .post<CreateChatCompletionForTutorIAV2_Res>(url, body, apiV3Options)
      .pipe(
        map((res) => res.response),
        catchError((error) => {
          console.error(
            'Error getting response from TutorIA AI Agent: ',
            error,
          );
          throw error;
        }),
      );

    return res;
  }

  generateTutorIAResponseV3(
    body: CreateChatCompletionForTutorIAV3_Req,
    token: string,
  ) {
    const url = `${API_V3_URL}/tutoria-ai/v3/chat`;
    const apiV3Options = this.getHttpOptionsV3WithToken(token);

    const res = this.http
      .post<CreateChatCompletionForTutorIAV3_Res>(url, body, apiV3Options)
      .pipe(
        map((res) => res.response),
        catchError((error) => {
          console.error(
            'Error getting response from TutorIA AI Agent: ',
            error,
          );
          throw error;
        }),
      );

    return res;
  }

  /** Get all TutorIA Chat Sessions for a User
   * @param body Body of the request
   * @param token User Token for validation
   * @returns An array of objects with the Chat Sessions
   */
  getAllChatSessionsForTutorIA(
    query: GetAllChatSessionsForTutorIA_Req,
    token: string,
  ): Observable<ChatSessionForList[]> {
    const url = `${API_V3_URL}/tutoria-ai/chat-sessions`;
    const queryParameters = new HttpParams({
      fromObject: query,
    });
    const apiV3Options = {
      ...this.getHttpOptionsV3WithToken(token),
      params: queryParameters,
    };

    const res = this.http
      .get<GetAllChatSessionsForTutorIA_Res>(url, apiV3Options)
      .pipe(
        map((res) => res.response.chat_sessions ?? []),
        catchError((error) => {
          //* If 404 then empty string since its not an actual error
          if (error.status == 404) {
            return of([] as ChatSessionForList[]);
          } else {
            console.error(
              'Error retrieving the TutorIA Chat Sessions of a User: ',
              error,
            );
            throw error;
          }
        }),
      );

    return res;
  }

  /** Get the data of one Chat Sessions from its Id
   * @param query Body of the request
   * @param token User Token for validation
   * @returns The Chat Session as an object
   */
  getChatSessionForTutorIA(
    query: GetChatSessionForTutorIA_Req,
    chatSessionId: string,
    token: string,
  ) {
    const url = `${API_V3_URL}/tutoria-ai/chat-sessions/${encodeURIComponent(
      chatSessionId,
    )}`;
    const queryParameters = new HttpParams({
      fromObject: query,
    });
    const apiV3Options = {
      ...this.getHttpOptionsV3WithToken(token),
      params: queryParameters,
    };

    const res = this.http
      .get<GetChatSessionForTutorIA_Res>(url, apiV3Options)
      .pipe(
        map((res) => res.response),
        catchError((error) => {
          console.error('Error retrieving a TutorIA Chat Session: ', error);
          throw error;
        }),
      );

    return res;
  }
}

//$ API REQUEST AND RESPONSE TYPES
//* ---- Create Chat Completion For TutorIA v2 ----
export interface CreateChatCompletionForTutorIAV2_Req {
  chat_session_id: string;
  messages: {
    role: 'user' | 'assistant';
    content: string;
  }[];
  content_type_id: string;
  content_subtype_id: string;
  user_id: string;
  teamwork_user_ids: string[];
  mock_open_ai_response?: boolean;
}

export interface CreateChatCompletionForTutorIAV2_Res {
  response: {
    assistant_response: string;
  };
}

//* ---- Create Chat Completion For TutorIA v3 ----
export interface CreateChatCompletionForTutorIAV3_Req {
  chat_session_id: string;
  messages: {
    role: 'user' | 'assistant';
    content: string;
  }[];
  content_type_id: string;
  modifier_ids: string[] | null;
  user_id: string;
  teamwork_user_ids: string[];
  mock_open_ai_response?: boolean;
}

export interface CreateChatCompletionForTutorIAV3_Res {
  response: {
    assistant_response: string;
  };
}

//* ---- Get All Chat Sessions For TutorIA ----
export interface GetAllChatSessionsForTutorIA_Req {
  user_id: string;
  [param: string]:
    string | number | boolean | readonly (string | number | boolean)[];
}
export interface GetAllChatSessionsForTutorIA_Res {
  response: {
    chat_sessions: ChatSessionForList[];
  };
}
export interface ChatSessionForList {
  chat_session_id: string;
  title: string;
  creation_date: Date;
  last_update: Date;
}

//* ---- Get Chat Session For TutorIA ----
export interface GetChatSessionForTutorIA_Req {
  user_id: string;
  [param: string]:
    string | number | boolean | readonly (string | number | boolean)[];
}

export interface GetChatSessionForTutorIA_Res {
  response: {
    title: string;
    creation_date: Date;
    last_update: Date;
    content_type_id: string;
    content_subtype_id: string;
    messages: {
      role: 'user' | 'assistant';
      content: string;
    }[];
  };
}
